/**
 * The day, read from the database rather than counted in the browser.
 *
 * `worklist_summary` computes every group over the caller's **whole** visible
 * book, using the same `book_follow_up_category()` that `book_matches()` uses
 * — so a card's figure and the book filter it links to are the same question,
 * and `supabase/tests/12` asserts exactly that against `book_totals()`.
 *
 * The reference implementation's `worklistSummary()` takes an array of
 * customers and adds them up. That is the right shape for a small list already
 * on screen and the wrong shape for this: a morning screen that counted the
 * first page would tell a collector with 1,800 accounts that they have nothing
 * to do, and would be most wrong for the people with the most work.
 *
 * Everything here is `security invoker`, so a collector's day is their own
 * accounts and a manager's is the book they may see. No client-side filtering
 * stands anywhere in it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { readWorklistSummary, type TenantContext, type WorklistTotals } from '@timely/domain';
import { supabase } from '../lib/supabaseClient';
import { emptyChequeTotals, type ChequeTotals } from '../cheques/useCheques';

/** One colleague's share of the work, for a manager's Today. */
export interface MemberWorkload {
    memberId: string;
    displayName: string;
    overdueCount: number;
    todayCount: number;
    urgentCount: number;
    openCount: number;
    openAmount: number;
}

export interface TodayData {
    totals: WorklistTotals | null;
    team: MemberWorkload[];
    cheques: ChequeTotals | null;
    /** True when the cheque figures were refused, not merely empty. */
    chequesUnavailable: boolean;
}

const EMPTY: TodayData = { totals: null, team: [], cheques: null, chequesUnavailable: false };

export function useToday(context: TenantContext, options: { withTeam: boolean; withCheques: boolean }) {
    const workspaceId = context.workspace.id;
    const [data, setData] = useState<TodayData>(EMPTY);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const { withTeam, withCheques } = options;
    const key = JSON.stringify([workspaceId, withTeam, withCheques]);
    const issuedFor = useRef(key);

    const load = useCallback(async () => {
        issuedFor.current = key;
        setLoading(true);
        setError(null);

        const client = supabase();
        const [summary, team, cheques] = await Promise.all([
            client.rpc('worklist_summary', { p_workspace: workspaceId }),
            withTeam
                ? client.rpc('worklist_by_member', { p_workspace: workspaceId })
                : Promise.resolve({ data: null, error: null }),
            // The same call the register makes with no filters, so Today's
            // cheque figures and the register's tiles cannot disagree.
            withCheques
                ? client.rpc('cheque_totals', { p_workspace: workspaceId })
                : Promise.resolve({ data: null, error: null }),
        ]);

        if (issuedFor.current !== key) return;

        if (summary.error) {
            setError('We could not load your day. Try again in a moment.');
            setLoading(false);
            return;
        }

        const summaryRow = (Array.isArray(summary.data) ? summary.data[0] : summary.data) as
            Record<string, unknown> | null;
        const chequeRow = (Array.isArray(cheques.data) ? cheques.data[0] : cheques.data) as
            Record<string, unknown> | null;

        setData({
            totals: readWorklistSummary(summaryRow),
            team: ((team.data ?? []) as Record<string, unknown>[]).map(row => ({
                memberId: String(row['member_id']),
                displayName: String(row['display_name'] ?? 'Somebody'),
                overdueCount: Number(row['overdue_count']) || 0,
                todayCount: Number(row['today_count']) || 0,
                urgentCount: Number(row['urgent_count']) || 0,
                openCount: Number(row['open_count']) || 0,
                openAmount: Number(row['open_amount']) || 0,
            })),
            cheques: withCheques && !cheques.error && chequeRow
                ? {
                    matchedCount: Number(chequeRow['matched_count']) || 0,
                    matchedAmount: Number(chequeRow['matched_amount']) || 0,
                    visibleCount: Number(chequeRow['visible_count']) || 0,
                    byState: {
                        due: figure(chequeRow, 'due'),
                        overdue: figure(chequeRow, 'overdue'),
                        upcoming: figure(chequeRow, 'upcoming'),
                        hold: figure(chequeRow, 'hold'),
                        bounced: figure(chequeRow, 'bounced'),
                        cleared: figure(chequeRow, 'cleared'),
                    },
                }
                : withCheques ? emptyChequeTotals() : null,
            chequesUnavailable: withCheques && Boolean(cheques.error),
        });
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    useEffect(() => { void load(); }, [load]);

    return { ...data, loading, error, reload: load };
}

const figure = (row: Record<string, unknown>, state: string) => ({
    count: Number(row[`${state}_count`]) || 0,
    amount: Number(row[`${state}_amount`]) || 0,
});
